import { HTTPError } from 'ky';
import type { LccPrice } from './types';

/**
 * `operator-bff:approve-at-price@v1` refusals (commercial-contract §6.14, D16, D17). A purchase
 * sends the PRICE the operator approved; the BFF re-reads the cost and answers:
 *  - 409 `{ code: 'price_changed', currentPriceMinor, currency }` — the price moved; show the
 *    new one and ask to approve again (nothing was bought);
 *  - 503 `{ code: 'price_unavailable' }` — no price can be confirmed now; nothing was bought.
 * The console names them `PRICE_CHANGED` / `PRICE_UNAVAILABLE`, like every other safe code.
 */
export const PRICE_CHANGED = 'PRICE_CHANGED';
export const PRICE_UNAVAILABLE = 'PRICE_UNAVAILABLE';

/**
 * The quote a `price_changed` refusal names when the server had to RE-QUOTE the purchase (another
 * group's purchase had used the provider's offer): the operator approves THAT quote.
 */
export type RequotedOffer = { id: string; expiresAt?: string };

export type PriceRefusal =
  | { code: typeof PRICE_CHANGED; current: LccPrice; offer?: RequotedOffer }
  | { code: typeof PRICE_UNAVAILABLE };

/** 13002 → "130.02". */
export function minorToAmount(minor: number): string {
  const abs = Math.abs(Math.trunc(minor));
  return `${minor < 0 ? '-' : ''}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

export function priceRefusalOf(body: unknown): PriceRefusal | null {
  if (!body || typeof body !== 'object') return null;
  const b = body as { code?: unknown; currentPriceMinor?: unknown; currency?: unknown; offerId?: unknown; expiresAt?: unknown };
  if (b.code === 'price_unavailable') return { code: PRICE_UNAVAILABLE };
  if (
    b.code === 'price_changed' &&
    typeof b.currentPriceMinor === 'number' &&
    Number.isSafeInteger(b.currentPriceMinor) &&
    b.currentPriceMinor > 0 &&
    typeof b.currency === 'string' &&
    /^[A-Z]{3}$/.test(b.currency)
  ) {
    const requoted =
      typeof b.offerId === 'string' && /^[A-Za-z0-9-]{8,100}$/.test(b.offerId)
        ? {
            id: b.offerId,
            ...(typeof b.expiresAt === 'string' && Number.isFinite(Date.parse(b.expiresAt)) ? { expiresAt: b.expiresAt } : {}),
          }
        : undefined;
    return {
      code: PRICE_CHANGED,
      current: { amount: minorToAmount(b.currentPriceMinor), currency: b.currency },
      ...(requoted ? { offer: requoted } : {}),
    };
  }
  return null;
}

// Captured in each client's afterResponse hook, before the shared beforeError consumes the body.
const changes = new WeakMap<Response, { current: LccPrice; offer?: RequotedOffer }>();

/** Reads a refusal off a failed response; returns its console code (and keeps the new price). */
export function notePriceRefusal(response: Response, body: unknown): string | undefined {
  const refusal = priceRefusalOf(body);
  if (!refusal) return undefined;
  if (refusal.code === PRICE_CHANGED) changes.set(response, { current: refusal.current, ...(refusal.offer ? { offer: refusal.offer } : {}) });
  return refusal.code;
}

/** The current price a `PRICE_CHANGED` refusal carried, if `error` is one. */
export function currentPriceOf(error: unknown): LccPrice | null {
  return error instanceof HTTPError ? (changes.get(error.response)?.current ?? null) : null;
}

/** The NEW quote a `PRICE_CHANGED` refusal named (the purchase was re-quoted), if any. */
export function requotedOfferOf(error: unknown): RequotedOffer | null {
  return error instanceof HTTPError ? (changes.get(error.response)?.offer ?? null) : null;
}
